import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import 'core-js/proposals/set-methods-v2';
import type { KeywordSet } from '@rsos/reduxDataStore';
import { KeywordAPI, keywordSetsSelector } from '@rsos/reduxDataStore';

export const useKeywordRecommendations = (
  keywordSet: KeywordSet,
): {
  /**
   * A dynamically-changing list of suggested keywords
   */
  recommendations: string[];
  /**
   * When the user selects a keyword, remove it from the list of suggestions
   */
  removeRecommendation: (word: string) => void;
  /**
   * pauseRecommendations while the user is interacting, so we don't swap their
   * options out from under their mouse.
   */
  pauseRecommendations: () => void;
  startRecommendations: () => void;
  /**
   * If you want to display a spinner or other indicator that new recs might be
   * coming, use this flag.
   */
  thinking: boolean;
} => {
  const api = new KeywordAPI();

  // Lingohub does not return a stable set of recommendations; the same inputs
  // can yield different results. So most of the complexity of this hook is
  // involved in making sure we only fetch more results when that's good for the
  // user.
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const loadingHash = useRef('');
  const paused = useRef(false);

  // Many things can re-call this hook, but we should only fetch new keywords if
  // the input set has changed since last time. The keyword's identity doesn't
  // necessarily track this, but the string sum of all the keywords and the name
  // will always match what we're interested in.
  const oldHash = useRef('');
  const hash = keywordSet.keywords.join() + keywordSet.name;

  // We don't want to display any suggestions the conflict with other keywords
  // in other sets.
  const keywordSets = useSelector(keywordSetsSelector).keywordSets || [];
  const allExisingKeywords = keywordSets
    .map(k =>
      // Skip the current set, we'll use its unsaved keywords instead
      k.id === keywordSet.id ? new Set(keywordSet.keywords) : new Set<string>(),
    )
    .reduce((a, b) => a.union(b), new Set(keywordSet.keywords));

  // If the set has changed since the last time we made suggestions, fetch new
  // suggestions.
  const fetchRecommendations = useCallback(() => {
    if (hash === oldHash.current) return;

    loadingHash.current = hash;
    api.fetchKeywordRecommendations(keywordSet).then(recs => {
      if (hash !== loadingHash.current) return;
      const noRepeats = new Set(recs).difference(allExisingKeywords);
      oldHash.current = hash;
      loadingHash.current = '';

      // Temporary, but should be deployed to CI/QA: print the literal
      // suggestions for Leah to consider as she works on the prompt
      // eslint-disable-next-line no-console
      console.log(recs);

      // If paused, you can add to the end of the list, but never change what
      // they might be considering clicking on
      if (paused.current) {
        setRecommendations([
          ...recommendations,
          ...Array.from(noRepeats.difference(new Set(recommendations))),
        ]);
      } else {
        setRecommendations(Array.from(noRepeats));
      }
    });
    // We don't need or want API, here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allExisingKeywords, hash, keywordSet, recommendations]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  // If the user picks a recommendation, let the component remove it from the
  // suggestion list immediately, even if we're paused.
  const removeRecommendation = (word: string) => {
    setRecommendations(recs => recs.filter(w => w !== word));
  };

  return {
    recommendations: recommendations.slice(0, 8),
    removeRecommendation,
    pauseRecommendations: () => {
      paused.current = true;
    },
    startRecommendations: () => {
      paused.current = false;
      fetchRecommendations();
    },
    thinking: !!loadingHash,
  };
};

// These methods are available in modern browsers, and via core-js for older
// browsers, but aren't typed. Feel free to remove this when the time comes that
// it is unnecessary.
declare global {
  interface Set<T> {
    union(other: Set<T>): Set<T>;
    difference(other: Set<T>): Set<T>;
  }
}
